import Card from '@pelckmans/elementis/components/card';
import Page from '@pelckmans/elementis/components/page';
import Pill from '@pelckmans/elementis/components/pill';
import Results from '@pelckmans/elementis/icons/results';
import Search from '@pelckmans/elementis/icons/search';
import Tasks from '@pelckmans/elementis/icons/tasks';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import IdentityContext from '../../../contexts/IdentityContext';
import MyAssignmentsContext from '../../../contexts/MyAssignmentsContext';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useRoutes } from '../../../hooks/useRoutes';
import ContentBody from '../../../layout/content/ContentBody';
import ContentHeader from '../../../layout/content/ContentHeader';
import ModuleBreadcrumbs from '../../../layout/header/ModuleBreadcrumbs';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../../shared-components/elementis/hoc/withScrollThreshold';
import AppsContext from '../_contexts/AppsContext';
import ModuleContext from '../_contexts/ModuleContext';
import DigibookButton from '../components/DigibookButton';
import { isDigibookApp, isOldDigibookApp } from '../utils/digibookAppUtils';
import ModuleCard from './components/ModuleCard';

interface SubModuleSelectorProps extends WithScrollThresholdProps {}

const getAssignmentsTag = (count: number) => ({
  role: undefined,
  'aria-label': undefined,
  children: (
    <Pill role="listitem" prominence="danger" key="assignment-count">
      {count}
    </Pill>
  ),
});

function SubModuleSelector({ pastThreshold }: SubModuleSelectorProps) {
  const { module: superModule, subModules } = useContext(ModuleContext);
  const { assignments } = useContext(MyAssignmentsContext);
  const { apps } = useContext(AppsContext);
  const { identity } = useContext(IdentityContext);

  const { buildRelativePath, buildAbsolutePath } = useRoutes();
  const { t } = useTranslation();

  const digibooksApps = apps.filter(app => isDigibookApp(app) || isOldDigibookApp(app));
  const { isTeacherAlike } = identity;

  useDocumentTitle(t('PAGES.SUBMODULE_SELECTOR.TITLE'));

  if (!superModule) return null;
  if (superModule && !superModule.hasExerciseEntrance && !superModule.subModules.length) {
    return <Navigate to={buildRelativePath('MATERIAL.TOC', { selectedModuleSlug: superModule.slug, nodeId: '' })} />;
  }

  const params = {
    subjectSlug: superModule?.subjectSlug || '',
    methodSlug: superModule?.methodSlug || '',
    moduleSlug: superModule?.slug || '',
  };

  const size = pastThreshold ? 'sm' : 'md';

  return (
    <Page>
      <ContentHeader
        cover={
          <Page.Header
            density={pastThreshold ? 'compact' : 'normal'}
            headerText={t('PAGES.SUBMODULE_SELECTOR.PAGE_TITLE')}
            breadcrumbs={<ModuleBreadcrumbs size={size} />}
            buttonGroupProps={{
              'aria-label': undefined,
              role: 'group',
              children: <DigibookButton digibookApps={digibooksApps} size={size} />,
            }}
          />
        }
      />
      <ContentBody>
        <div className="sub-module-selector__card-list">
          <ModuleCard module={superModule} />
          {subModules.map(subModule => (
            <ModuleCard key={subModule.id} module={subModule} />
          ))}
          {superModule.hasExerciseEntrance && <ModuleCard module={superModule} isVirtual />}
        </div>
        <section className="sub-module-selector__shortcuts" data-testid="shortcuts">
          <div className="block-header">
            <h2 className="block-header__title">{t('PAGES.SUBMODULE_SELECTOR.SHORTCUTS.TITLE')}</h2>
          </div>
          <div className="sub-module-selector__shortcuts__list">
            <Card
              as="link"
              titleTag="h4"
              titleText={t('PAGES.SUBMODULE_SELECTOR.SHORTCUTS.CARDS.ASSIGNMENTS')}
              visualIcon={<Tasks />}
              href={buildAbsolutePath(['MATERIAL', 'MATERIAL.ASSIGNMENTS'], params)}
              tags={assignments.length ? getAssignmentsTag(assignments.length) : undefined}
            />
            {isTeacherAlike && (
              <Card
                as="link"
                titleTag="h4"
                titleText={t('PAGES.SUBMODULE_SELECTOR.SHORTCUTS.CARDS.RESULTS')}
                visualIcon={<Results />}
                href={buildAbsolutePath(['MATERIAL', 'MATERIAL.RESULTS'], params)}
              />
            )}
            <Card
              as="link"
              titleTag="h4"
              titleText={t('PAGES.SUBMODULE_SELECTOR.SHORTCUTS.CARDS.SEARCH')}
              visualIcon={<Search />}
              href={buildAbsolutePath(['MATERIAL', 'MATERIAL.SEARCH'], params)}
            />
          </div>
        </section>
      </ContentBody>
    </Page>
  );
}

export default withScrollThreshold(SubModuleSelector);
